import { useMemo, useState } from 'react';
import { cn } from 'lib/cn';
import { PageHeader } from 'components/ui';
import { LoadingState, ErrorState, EmptyState } from 'components/feedback';
import { ReceiptIcon } from 'components/icons';
import { useToast } from 'components/toast/ToastProvider';
import { useVendorOrders } from '../hooks/useVendorOrders';
import { useRejectionReasons, useRejectOrder } from '../hooks/useRejectOrder';
import { useMarkOrderReady } from '../hooks/useMarkOrderReady';
import { OrderItemCard } from '../components/OrderItemCard';
import { RejectOrderModal } from '../components/RejectOrderModal';
import { ORDER_TABS, flattenOrders, tabForStatus } from '../utils/orderStatus';

/** Item-centric orders screen with status filter tabs. */
export function OrdersPage() {
  const { orders, loading, error, refetch, truncated } = useVendorOrders();
  const { reasons } = useRejectionReasons();
  const { rejectOrder, loading: rejecting } = useRejectOrder();
  const { markReady } = useMarkOrderReady();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('received');
  const [rejectingItem, setRejectingItem] = useState(null);
  const [markingIds, setMarkingIds] = useState(() => new Set());

  const flatItems = useMemo(() => flattenOrders(orders), [orders]);

  const counts = useMemo(() => {
    const result = {};
    flatItems.forEach(({ item }) => {
      const tab = tabForStatus(item.status);
      if (tab) result[tab] = (result[tab] || 0) + 1;
    });
    return result;
  }, [flatItems]);

  const visibleItems = useMemo(() => {
    const list = flatItems.filter(({ item }) => tabForStatus(item.status) === activeTab);
    const ascending = activeTab === 'received'; // oldest first for fulfillment
    return [...list].sort((a, b) => {
      const da = new Date(a.order.createdAt).getTime() || 0;
      const db = new Date(b.order.createdAt).getTime() || 0;
      return ascending ? da - db : db - da;
    });
  }, [flatItems, activeTab]);

  const handleMarkReady = async (item) => {
    const id = item.id;
    setMarkingIds((prev) => new Set(prev).add(id));
    try {
      const result = await markReady(id);
      if (result.ok) {
        toast.success('Order ready', result.message || 'Marked ready for pickup.');
        refetch();
      } else {
        toast.error('Could not mark ready', result.message || 'Please try again.');
      }
    } catch (err) {
      toast.error('Something went wrong', err.message);
    } finally {
      setMarkingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleConfirmReject = async (reason) => {
    try {
      const result = await rejectOrder({ orderItemId: rejectingItem.id, reason });
      if (result?.success) {
        toast.success('Order item rejected', result.message);
        setRejectingItem(null);
        refetch();
      } else {
        toast.error(
          'Could not reject',
          result?.errors?.[0]?.message || result?.message || 'Please try again.',
        );
      }
    } catch (err) {
      toast.error('Something went wrong', err.message);
    }
  };

  let content;
  if (loading && orders.length === 0) {
    content = <LoadingState label="Loading your orders…" />;
  } else if (error && orders.length === 0) {
    content = <ErrorState error={error} onRetry={() => refetch()} />;
  } else if (visibleItems.length === 0) {
    content = (
      <EmptyState
        icon={ReceiptIcon}
        title="Nothing here"
        description={`No ${activeTab} orders right now.`}
      />
    );
  } else {
    content = (
      <div className="space-y-3">
        {visibleItems.map((entry) => (
          <OrderItemCard
            key={entry.item.id}
            entry={entry}
            tab={activeTab}
            onReject={setRejectingItem}
            onMarkReady={handleMarkReady}
            isMarkingReady={markingIds.has(entry.item.id)}
          />
        ))}
        {truncated && (
          <p className="pt-2 text-center text-xs text-slate-400">Showing the most recent orders.</p>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Orders" description="Orders placed with your shop." />

      <div className="mb-5 flex gap-1 overflow-x-auto rounded-lg border border-surface-200 bg-white p-1">
        {ORDER_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'flex-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition',
              activeTab === tab.key
                ? 'bg-brand-600 text-white'
                : 'text-slate-600 hover:text-slate-900',
            )}
          >
            {tab.label}
            {counts[tab.key] ? ` (${counts[tab.key]})` : ''}
          </button>
        ))}
      </div>

      {content}

      <RejectOrderModal
        key={rejectingItem?.id}
        item={rejectingItem}
        reasons={reasons}
        loading={rejecting}
        onConfirm={handleConfirmReject}
        onClose={() => setRejectingItem(null)}
      />
    </div>
  );
}

export default OrdersPage;
